import React, { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { Plus, Search, Snowflake, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import {
  listSemenBatches, deleteSemenBatch, darEntradaDeSemen, achaPartida,
  agruparPorTouro, resumoDoSemen, emDoses, dataCurta,
} from '../../../data/semen';
import { useAnimals } from '../../../hooks/useAnimals';
import IdentidadeDoAnimal from './IdentidadeDoAnimal';
import SaidaDeSemen from './SaidaDeSemen';
import ExtratoDaPartida from './ExtratoDaPartida';
import { MARK } from '../../../constants';
import { enderecoDoBotijao } from '../../../data/estoque';

const hoje = () => new Date().toISOString().slice(0, 10);

const VAZIO = {
  bull: null, clientId: '', codigo: '', fornecedor: '', doses: '',
  data: hoje(), gotas_por_dose: '', botijao: '', caneca: '', rack: '', altura: '',
};

const input = 'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const Campo = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
    {children}
  </div>
);

const COLUNAS = 11;

/**
 * Cadastro, saldo e movimentação das partidas de sêmen.
 *
 * O touro é escolhido do cadastro de animais, não digitado: a coluna é FK e
 * recusa nome solto. É de propósito — o resto do schema guarda touro como
 * texto em quatro tabelas, e é por isso que "REM" e "Rem " viram dois touros
 * no relatório. O estoque nasce sem esse problema.
 *
 * A PARTIDA É UM SALDO, NÃO UM CONTADOR. Cada entrada e cada saída é um
 * lançamento com data, no extrato que abre em cada linha. Dar entrada do mesmo
 * sêmen duas vezes SOMA na mesma partida — antes batia no índice único e a
 * entrada simplesmente não entrava.
 *
 * Quem baixa o estoque pela bancada é a FIV, ao lançar as palhetas
 * descongeladas na sessão. Aqui entram as remessas e as saídas que não passam
 * pela bancada: venda, devolução ao dono, IA, perda.
 */
const EstoqueDeSemen = ({ laboratoryId, clients = [], podeMexer = false }) => {
  const { searchAnimals } = useAnimals(laboratoryId);

  const [partidas, setPartidas] = useState([]);
  const [nova, setNova]         = useState(VAZIO);
  const [busca, setBusca]       = useState('');
  const [achados, setAchados]   = useState([]);
  const [erro, setErro]         = useState('');
  const [salvando, setSalvando] = useState(false);
  const [saindo, setSaindo]     = useState(null);
  const [aberta, setAberta]     = useState(null);

  const carregar = useCallback(async () => {
    try {
      setPartidas(await listSemenBatches(laboratoryId) || []);
    } catch (e) {
      console.error('Erro ao carregar estoque de sêmen:', e);
      setErro(e.message);
    }
  }, [laboratoryId]);

  useEffect(() => { if (laboratoryId) carregar(); }, [laboratoryId, carregar]);

  const ordenados = useMemo(
    () => [...clients].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [clients]);

  const grupos = useMemo(() => agruparPorTouro(partidas), [partidas]);
  const resumo = useMemo(() => resumoDoSemen(partidas), [partidas]);

  /**
   * A partida que esta entrada vai engordar, se já existir.
   *
   * Calculado enquanto se digita, e não só ao salvar, porque é a resposta à
   * dúvida de quem está com a caixa de palhetas na mão: isto é sêmen novo ou
   * mais do que já tenho? A tela diz antes, com o saldo de agora e o de
   * depois.
   */
  const jaExiste = useMemo(() => (nova.bull
    ? achaPartida(partidas, {
        bullId: nova.bull.id, codigo: nova.codigo, clientId: nova.clientId || null,
      })
    : null), [partidas, nova.bull, nova.codigo, nova.clientId]);

  const buscarTouro = async (termo) => {
    setBusca(termo);
    setNova(n => ({ ...n, bull: null }));
    if (termo.length < 2) { setAchados([]); return; }
    const r = await searchAnimals(termo);
    setAchados(r.filter(a => a.category === 'Reprodutor' || a.category === 'Touro' || a.sex === 'M'));
  };

  const escolherTouro = (a) => {
    setNova(n => ({ ...n, bull: a }));
    setBusca(a.identifier);
    setAchados([]);
  };

  const adicionar = async () => {
    setSalvando(true);
    setErro('');
    try {
      await darEntradaDeSemen({
        laboratoryId,
        partidas,
        bullId:       nova.bull?.id,
        clientId:     nova.clientId || null,
        codigo:       nova.codigo,
        fornecedor:   nova.fornecedor,
        doses:        nova.doses,
        data:         nova.data,
        gotasPorDose: nova.gotas_por_dose,
        botijao:      nova.botijao,
        caneca:       nova.caneca,
        rack:         nova.rack,
        // '' violaria o check; nulo é o "não informado" que ele aceita.
        altura:       nova.altura,
      });
      setNova({ ...VAZIO, data: nova.data });
      setBusca('');
      await carregar();
    } catch (e) {
      // 23505 é o índice de partida única. Com o `achaPartida` no lugar ele não
      // deveria aparecer mais — se aparecer, a regra da tela e a do banco
      // divergiram, e é isso que a mensagem precisa dizer.
      setErro(e.code === '23505'
        ? 'Já existe uma partida deste touro com este código e este dono, e a entrada não somou nela. Recarregue a tela e tente de novo.'
        : e.message);
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (p) => {
    if (!window.confirm(
      `Excluir a partida ${p.touro}${p.codigo ? ` · ${p.codigo}` : ''}?\n\n`
      + 'Some com o histórico de entradas dela. Só é possível se nunca foi usada '
      + 'numa FIV e nunca teve saída lançada — para partida acabada, use Saída.')) return;
    try {
      await deleteSemenBatch(p.semen_batch_id);
      await carregar();
    } catch (e) {
      // 23503 = a FK RESTRICT de fiv_semen_uso segurou. O gatilho de saída
      // lançada já vem com a explicação escrita.
      setErro(e.code === '23503'
        ? 'Esta partida já foi usada numa FIV e por isso não pode ser excluída. Use Saída.'
        : e.message);
    }
  };

  const podeAdicionar = !!nova.bull && Number(nova.doses) > 0 && !!nova.data && !salvando;

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="px-6 py-4 border-b flex items-center space-x-3">
        <Snowflake className="text-slate-400" size={20} />
        <div className="flex-1">
          <h3 className="text-lg font-medium text-slate-900">Estoque de sêmen</h3>
          <p className="text-sm text-slate-600">
            Partidas e saldo em doses. Cada entrada e cada saída fica registrada com data;
            o que é descongelado numa FIV sai daqui sozinho.
          </p>
        </div>
        {partidas.length > 0 && (
          <div className="text-right shrink-0">
            <p className="text-lg font-semibold text-slate-900 leading-tight">
              {emDoses(resumo.doses)} <span className="text-sm font-normal text-slate-500">doses</span>
            </p>
            <p className="text-xs text-slate-500">
              {resumo.touros} touro{resumo.touros === 1 ? '' : 's'}
              {resumo.deClientes > 0 && ` · ${emDoses(resumo.deClientes)} de clientes`}
            </p>
          </div>
        )}
      </div>

      {podeMexer && (
        <div className="px-6 py-4 border-b bg-slate-50">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Campo label="Touro *" className="relative">
              <input type="text" value={busca} placeholder="Buscar no cadastro"
                onChange={(e) => buscarTouro(e.target.value)}
                className={`${input} pr-8`}
              />
              <Search className="absolute right-2 top-8 w-4 h-4 text-slate-400 pointer-events-none" />
              {achados.length > 0 && (
                <div className="absolute z-20 left-0 top-full mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {achados.map(a => (
                    <button key={a.id} onClick={() => escolherTouro(a)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b border-slate-100 last:border-0">
                      <IdentidadeDoAnimal animal={a} />
                    </button>
                  ))}
                </div>
              )}
              {busca.length >= 2 && !nova.bull && achados.length === 0 && (
                <p className="mt-1 text-xs text-slate-500">
                  Nenhum reprodutor com esse nome. Cadastre o touro em Animais primeiro.
                </p>
              )}
            </Campo>

            {/* Nulo é "do laboratório", não "não informado". Sêmen comprado pelo
                lab é a situação normal, e obrigar a escolher um cliente para
                ele faria inventar vínculo. */}
            <Campo label="Dono do sêmen">
              <select className={input} value={nova.clientId}
                onChange={(e) => setNova({ ...nova, clientId: e.target.value })}>
                <option value="">Do laboratório</option>
                {ordenados.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Campo>

            <Campo label="Código da partida">
              <input type="text" className={input} value={nova.codigo} placeholder="O da etiqueta"
                onChange={(e) => setNova({ ...nova, codigo: e.target.value })} />
            </Campo>

            <Campo label="Fornecedor">
              <input type="text" className={input} value={nova.fornecedor}
                onChange={(e) => setNova({ ...nova, fornecedor: e.target.value })} />
            </Campo>

            <Campo label="Doses (palhetas) *">
              <input type="number" min="0.5" step="0.5" className={input} value={nova.doses}
                onChange={(e) => setNova({ ...nova, doses: e.target.value })} />
            </Campo>

            {/* A data do fato, não a do lançamento: sêmen que chegou sexta e foi
                digitado na segunda entra com sexta. */}
            <Campo label="Data da entrada *">
              <input type="date" className={input} value={nova.data}
                onChange={(e) => setNova({ ...nova, data: e.target.value })} />
            </Campo>

            <Campo label="Gotas por dose">
              <input type="number" min="1" className={input} value={nova.gotas_por_dose}
                onChange={(e) => setNova({ ...nova, gotas_por_dose: e.target.value })} />
            </Campo>

            <Campo label="Botijão">
              <input type="text" className={input} value={nova.botijao}
                onChange={(e) => setNova({ ...nova, botijao: e.target.value })} />
            </Campo>

            <Campo label="Caneca">
              <input type="text" className={input} value={nova.caneca}
                onChange={(e) => setNova({ ...nova, caneca: e.target.value })} />
            </Campo>

            <Campo label="Rack">
              <input type="text" className={input} value={nova.rack}
                onChange={(e) => setNova({ ...nova, rack: e.target.value })} />
            </Campo>

            <Campo label="Altura no rack">
              <select className={input} value={nova.altura}
                onChange={(e) => setNova({ ...nova, altura: e.target.value })}>
                <option value="">Não informada</option>
                <option value="cima">Cima</option>
                <option value="baixo">Baixo</option>
              </select>
            </Campo>
          </div>

          {/* Dito antes de salvar, e não depois: é a diferença entre "isto é
              sêmen novo" e "isto é mais do que já tenho", que é a dúvida de
              quem está com a caixa de palhetas na mão. */}
          {nova.bull && Number(nova.doses) > 0 && (
            <p className="mt-3 text-xs text-slate-600">
              {jaExiste
                ? `${MARK.PENDING} Esta partida já existe com ${emDoses(jaExiste.saldo_doses)} `
                  + `dose(s)${enderecoDoBotijao(jaExiste) ? ` em ${enderecoDoBotijao(jaExiste)}` : ''}. `
                  + `A entrada soma: ficam ${emDoses(Number(jaExiste.saldo_doses || 0) + Number(nova.doses))}.`
                : `${MARK.PENDING} Partida nova: entram ${emDoses(nova.doses)} dose(s) de `
                  + `${nova.bull.identifier}${nova.clientId ? '' : ' (do laboratório)'}.`}
            </p>
          )}

          {erro && (
            <div className="mt-3 bg-white border border-slate-300 text-slate-700 text-sm rounded-lg px-3 py-2">
              {erro}
            </div>
          )}

          <button onClick={adicionar} disabled={!podeAdicionar}
            className="mt-3 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm
                       hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed">
            <Plus size={16} /> {jaExiste ? 'Somar ao estoque' : 'Dar entrada'}
          </button>
        </div>
      )}

      {partidas.length === 0 ? (
        <div className="px-6 py-10 text-center">
          <Snowflake className="mx-auto mb-3 text-slate-400" size={40} />
          <p className="text-slate-600 text-sm">Nenhuma partida lançada.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 border-b">
              <tr>
                <th className="px-6 py-2 text-left font-medium">Touro</th>
                <th className="px-3 py-2 text-left font-medium">Partida</th>
                <th className="px-3 py-2 text-left font-medium">Dono</th>
                <th className="px-3 py-2 text-left font-medium">Onde está</th>
                <th className="px-3 py-2 text-center font-medium">Entraram</th>
                <th className="px-3 py-2 text-center font-medium">Usadas</th>
                <th className="px-3 py-2 text-center font-medium">Saíram</th>
                <th className="px-3 py-2 text-center font-medium">Saldo</th>
                <th className="px-3 py-2 text-center font-medium">Últ. entrada</th>
                <th className="px-3 py-2 text-center font-medium">Últ. saída</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {grupos.map(g => (
                <Fragment key={g.bull_id || g.touro}>
                  {/* Subtotal só quando há mais de uma partida do mesmo touro.
                      Com uma só, repetir o número seria ruído. */}
                  {g.partidas.length > 1 && (
                    <tr className="bg-slate-50/70">
                      <td colSpan={COLUNAS} className="px-6 py-1.5 text-xs text-slate-600">
                        <span className="font-medium text-slate-800">{g.touro}</span>
                        {' — '}{emDoses(g.saldo)} dose{g.saldo === 1 ? '' : 's'} em{' '}
                        {g.partidas.length} partidas
                      </td>
                    </tr>
                  )}

                  {g.partidas.map(p => {
                    const saldo = Number(p.saldo_doses);
                    const abertaAqui = aberta === p.semen_batch_id;
                    return (
                      <Fragment key={p.semen_batch_id}>
                        <tr className="hover:bg-slate-50">
                          <td className="px-6 py-2 font-medium text-slate-900">
                            <button
                              onClick={() => setAberta(abertaAqui ? null : p.semen_batch_id)}
                              className="flex items-center gap-1 text-left hover:text-blue-700"
                              title="Ver a movimentação desta partida">
                              {abertaAqui ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              {p.touro || '—'}
                            </button>
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {p.codigo || '—'}
                            {p.fornecedor && <span className="text-slate-400"> · {p.fornecedor}</span>}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {p.dono || <span className="text-slate-400">do laboratório</span>}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {p.botijao
                              ? enderecoDoBotijao(p)
                              : <span className="text-slate-400">—</span>}
                            {/* Texto antigo, quando houver: é dado real e sumiria
                                da vista se a tela só olhasse os campos novos. */}
                            {!p.botijao && p.local_legado && (
                              <span className="text-xs text-slate-400 block">anotado: {p.local_legado}</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center text-slate-600">
                            {emDoses(p.doses_entradas)}
                          </td>
                          <td className="px-3 py-2 text-center text-slate-600">
                            {Number(p.doses_usadas) ? emDoses(p.doses_usadas) : '—'}
                          </td>
                          <td className="px-3 py-2 text-center text-slate-600">
                            {Number(p.doses_saidas) ? emDoses(p.doses_saidas) : '—'}
                          </td>
                          <td className="px-3 py-2 text-center font-semibold text-slate-900">
                            {emDoses(saldo)} {saldo <= 0 && <span title="Sem palheta">{MARK.WARN}</span>}
                          </td>
                          <td className="px-3 py-2 text-center text-slate-500 whitespace-nowrap">
                            {dataCurta(p.ultima_entrada) || '—'}
                          </td>
                          <td className="px-3 py-2 text-center text-slate-500 whitespace-nowrap">
                            {dataCurta(p.ultima_saida) || '—'}
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            {podeMexer && (
                              <>
                                <button onClick={() => setSaindo(p)}
                                  className="text-slate-500 hover:text-slate-900 text-xs px-2 py-1 rounded hover:bg-slate-100">
                                  Saída
                                </button>
                                <button onClick={() => excluir(p)} title="Excluir partida"
                                  className="text-slate-400 hover:text-slate-700 p-1 rounded hover:bg-slate-100">
                                  <Trash2 size={14} />
                                </button>
                              </>
                            )}
                          </td>
                        </tr>

                        {abertaAqui && (
                          <tr>
                            <td colSpan={COLUNAS} className="p-0">
                              <ExtratoDaPartida partida={p} podeMexer={podeMexer} onMudou={carregar} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {saindo && (
        <SaidaDeSemen
          partida={saindo}
          onPronto={carregar}
          onFechar={() => setSaindo(null)}
        />
      )}
    </div>
  );
};

export default EstoqueDeSemen;
